import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useLabels } from '../../hooks/useLabels';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import { useAuth } from '../../hooks/useAuth';
import { useRoleStore } from '../../stores/useRoleStore';
import { useThemeStore } from '../../stores/useThemeStore';
import { useTheme } from '../../contexts/ThemeContext';
import { useUIStateStore } from '../../stores/useUIStateStore';
import LogoutIcon from '../icons/LogoutIcon';
import MoonIcon from '../icons/MoonIcon';
import SunIcon from '../icons/SunIcon';
import NotificationCenter from '../notifications/NotificationCenter';
import { Bars3Icon } from '@heroicons/react/24/outline';
import LanguageSwitcher from './LanguageSwitcher';
import { Text as Typography } from './Text';
import { Fragment } from 'react';
import { Transition } from '@headlessui/react';

const SystemIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

// ─── Dropdown Identity — Single Source of Truth ────────────────────────────────
type ActiveDropdown = 'language' | 'notification' | 'profile' | null;

interface HeaderProps {
  pageTitle: string;
}

// ─── Live Clock Hook ───────────────────────────────────────────────────────────
function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ─── Component ─────────────────────────────────────────────────────────────────
const Header: React.FC<HeaderProps> = ({ pageTitle }) => {
  const { getLabel } = useLabels();
  const { user, logout } = useAuth();
  const navigate = ReactRouterDOM.useNavigate();
  const location = ReactRouterDOM.useLocation();
  const now = useLiveClock();

  // ── Unified dropdown state — mutual exclusion guaranteed ────────────────────
  const [activeDropdown, setActiveDropdown] = useState<ActiveDropdown>(null);
  const headerActionsRef = useRef<HTMLDivElement>(null);

  const { links: navLinks } = useAppNavigation();
  const { fetchRoles, roles } = useRoleStore();
  const { toggleMobileSidebar, activePageTitle } = useUIStateStore();
  const { theme, resolvedTheme, toggleTheme } = useThemeStore();
  const { theme: fullTheme } = useTheme();

  const isGlassEnabled = fullTheme?.aesthetics?.glassmorphism?.enabled;
  const isDark = resolvedTheme === 'dark';
  const headerBgColor = isDark
    ? fullTheme?.darkMode?.bgSecondary
    : fullTheme?.lightMode?.bgSecondary;

  useEffect(() => {
    if (user?.permissions?.includes('user:manage')) fetchRoles();
  }, [user, fetchRoles]);

  // ── Global click-outside ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeDropdown) return;
    const handler = (e: MouseEvent) => {
      if (headerActionsRef.current && !headerActionsRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeDropdown]);

  // ── Route change closes all dropdowns ────────────────────────────────────────
  useEffect(() => { setActiveDropdown(null); }, [location]);

  // ── Toggle: open requested, close all others ─────────────────────────────────
  const toggleDropdown = useCallback((id: ActiveDropdown) => {
    setActiveDropdown((prev) => (prev === id ? null : id));
  }, []);

  // ── Derived user display data ─────────────────────────────────────────────────
  const { userDisplayName, userRoleName } = useMemo(() => {
    if (!user) return { userDisplayName: '', userRoleName: '' };
    const roleDetails = roles.find((r) => r.key === user.role);
    const displayName =
      roleDetails?.headerDisplay === 'userFullName'
        ? user.fullName
        : roleDetails?.displayName || user.fullName;
    const roleName = roleDetails?.displayName || user.role || '';
    return { userDisplayName: displayName, userRoleName: roleName };
  }, [user, roles]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getProfilePath = () => {
    const link = navLinks.find((l) => l.name === 'nav_student_profile');
    return link?.path || '/';
  };

  // ── Live clock formatting ─────────────────────────────────────────────────────
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

  const isProfileOpen = activeDropdown === 'profile';

  const headerClass = `
    relative px-4 py-0 shadow-sm flex justify-between items-stretch flex-shrink-0
    border-b border-theme-divider z-50 transition-all duration-300
    ${isGlassEnabled ? 'glass-panel' : 'bg-theme-bg-secondary'}
    text-theme-sidebar-text
  `;

  return (
    <header className={headerClass} style={{ backgroundColor: headerBgColor }}>
      {/* ── Premium top accent line ───────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-theme-primary/0 via-theme-primary/60 to-theme-primary/0 pointer-events-none" />

      {/* ── Left: Mobile toggle + Page title ─────────────────────────── */}
      <div className="flex items-center py-3">
        <button
          onClick={toggleMobileSidebar}
          className="lg:hidden mr-3 p-1.5 rounded-lg text-theme-sidebar-icon hover:text-theme-primary hover:bg-theme-primary/10 transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none"
          aria-label="Open menu"
        >
          <Bars3Icon className="h-5 w-5 stroke-[2.5]" />
        </button>

        <div className="flex flex-col justify-center">
          <Typography variant="h2" className="header-text-style leading-tight">
            {pageTitle || activePageTitle}
          </Typography>
          {/* Subtle breadcrumb path */}
          <p className="hidden sm:block text-[11px] text-theme-text-secondary opacity-60 font-mono mt-0.5 leading-none">
            {location.pathname}
          </p>
        </div>
      </div>

      {/* ── Right: All actions in one focus group ─────────────────────── */}
      <div className="flex items-center gap-2 py-3" ref={headerActionsRef}>

        {/* ── Live clock (desktop only) ── */}
        <div className="hidden lg:flex flex-col items-end mr-1 select-none">
          <span className="text-sm font-bold tabular-nums text-theme-text-primary leading-none">
            {timeStr}
          </span>
          <span className="text-[10px] text-theme-text-secondary opacity-60 font-mono mt-0.5 leading-none">
            {dateStr}
          </span>
        </div>

        {/* ── Thin vertical separator ── */}
        <div className="hidden lg:block h-7 w-px bg-theme-divider opacity-40 mx-1 flex-shrink-0" aria-hidden="true" />

        {/* ── Language Switcher ── */}
        <LanguageSwitcher
          isOpen={activeDropdown === 'language'}
          onToggle={() => toggleDropdown('language')}
        />

        {/* ── Theme Toggle ── */}
        <button
          onClick={toggleTheme}
          className="inline-flex items-center justify-center p-2 rounded-full text-theme-sidebar-icon shadow-sm ring-1 ring-inset ring-theme-divider transition-all duration-300 hover:bg-theme-primary/10 hover:text-theme-primary hover:ring-theme-primary/40 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-theme-primary"
          aria-label={`Toggle theme (current: ${theme})`}
        >
          {theme === 'light' ? (
            <MoonIcon />
          ) : theme === 'dark' ? (
            <SystemIcon />
          ) : (
            <SunIcon />
          )}
        </button>

        {/* ── Notification Center ── */}
        <NotificationCenter
          isOpen={activeDropdown === 'notification'}
          onToggle={() => toggleDropdown('notification')}
        />

        {/* ── Thin vertical separator ── */}
        <div className="h-7 w-px bg-theme-divider opacity-40 mx-1 flex-shrink-0" aria-hidden="true" />

        {/* ── Profile Trigger ─────────────────────────────────────────── */}
        {user && (
          <div className="relative">
            <button
              onClick={() => toggleDropdown('profile')}
              className={`flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-all duration-300 active:scale-95 focus:outline-none group
                ${isProfileOpen
                  ? 'bg-theme-primary/10 ring-1 ring-theme-primary/30'
                  : 'hover:bg-theme-primary/5 ring-1 ring-transparent hover:ring-theme-divider'
                }`}
            >
              {/* Avatar with online status dot */}
              <div className="relative flex-shrink-0">
                <div
                  className={`w-8 h-8 bg-theme-primary rounded-full flex items-center justify-center text-white font-bold text-sm transition-all duration-300
                    ${isProfileOpen ? 'ring-2 ring-theme-primary/50 ring-offset-1 ring-offset-transparent' : 'group-hover:ring-2 group-hover:ring-theme-primary/30 ring-2 ring-transparent ring-offset-1 ring-offset-transparent'}`}
                >
                  {user.fullName?.charAt(0).toUpperCase()}
                </div>
                {/* Online status indicator */}
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success border-2 border-theme-bg-secondary">
                  <span className="absolute inset-0 rounded-full bg-success animate-ping opacity-60" />
                </span>
              </div>

              {/* Name + role */}
              <div className="hidden md:flex flex-col items-start min-w-0">
                <span className="text-sm font-semibold text-theme-text-primary leading-tight truncate max-w-[120px]">
                  {userDisplayName}
                </span>
                {userRoleName && (
                  <span className="text-[10px] font-medium text-theme-primary opacity-80 uppercase tracking-wider leading-none mt-0.5 truncate max-w-[120px]">
                    {userRoleName}
                  </span>
                )}
              </div>

              {/* Animated chevron */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-3.5 w-3.5 flex-shrink-0 text-theme-sidebar-icon opacity-50 transition-all duration-300 ease-in-out
                  ${isProfileOpen ? 'rotate-180 opacity-100 text-theme-primary' : 'group-hover:opacity-80'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* ── Profile Panel ─────────────────────────────────────── */}
            <Transition
              as={Fragment}
              show={isProfileOpen}
              enter="transition ease-out duration-300"
              enterFrom="transform opacity-0 -translate-y-2 scale-95"
              enterTo="transform opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-200"
              leaveFrom="transform opacity-100 translate-y-0 scale-100"
              leaveTo="transform opacity-0 -translate-y-2 scale-95"
            >
              <div className="absolute right-0 mt-3 w-64 bg-[rgb(var(--color-stats-container-bg-rgb))]/95 backdrop-blur-md rounded-xl shadow-2xl z-50 border border-theme-divider overflow-hidden origin-top-right">

                {/* User identity card */}
                <div className="px-4 pt-4 pb-3 border-b border-theme-divider">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 bg-theme-primary rounded-full flex items-center justify-center text-white font-bold text-base ring-2 ring-theme-primary/30">
                        {user.fullName?.charAt(0).toUpperCase()}
                      </div>
                      <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success border-2 border-theme-bg-secondary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-theme-text-primary truncate">
                        {user.fullName}
                      </p>
                      <p className="text-xs text-theme-text-secondary truncate opacity-80">
                        {user.email || ''}
                      </p>
                      {userRoleName && (
                        <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded-md bg-theme-primary/10 text-[10px] font-bold text-theme-primary uppercase tracking-wider">
                          {userRoleName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="py-1.5">
                  <ReactRouterDOM.Link
                    to={getProfilePath()}
                    onClick={() => setActiveDropdown(null)}
                    className="flex items-center px-4 py-2.5 text-sm font-medium text-theme-text-primary hover:bg-theme-primary/10 hover:text-theme-primary transition-all duration-200 group"
                  >
                    <div className="w-7 h-7 mr-3 rounded-lg bg-theme-primary/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <svg className="w-3.5 h-3.5 text-theme-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    {getLabel('header_my_profile')}
                  </ReactRouterDOM.Link>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left flex items-center px-4 py-2.5 text-sm font-medium text-danger hover:bg-danger/10 transition-all duration-200 group"
                  >
                    <div className="w-7 h-7 mr-3 rounded-lg bg-danger/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <LogoutIcon className="w-3.5 h-3.5 text-danger" />
                    </div>
                    {getLabel('header_logout')}
                  </button>
                </div>
              </div>
            </Transition>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
